import LinkHandle from "./LinkHandle.svelte";
import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { slugify } from "app/utils/strings";
import type { SvelteComponent } from "svelte";
import type { EditorView } from "@tiptap/pm/view";

export default Extension.create({
  name: "linkHandle",
  addOptions() {
    return {
      position: "absolute",
    };
  },
  addProseMirrorPlugins() {
    let linkElement: { element: Element | null; component: SvelteComponent } | null;

    const { position } = this.options;
    return [
      new Plugin({
        view() {
          return {
            update(view) {
              // only set heading ids in reader mode
              if (!view.editable) {
                setHeadingIds(view);
              }

              if (linkElement) {
                deleteLinkHandle(linkElement);
                linkElement = null;
                return false;
              }
            },
            destroy() {
              if (linkElement) {
                deleteLinkHandle(linkElement);
                linkElement = null;
              }
            },
          };
        },
        props: {
          handleDOMEvents: {
            mousemove(view, event: MouseEvent) {
              // disabled if document is in edit mode
              if (view.editable) return false;

              // add a 50px offset to add some 'safe area' to the left of the heading node
              // this prevents the link handle from being deleted and unreachable when hovering over the left edge of the heading node
              const coords = {
                left: event.clientX + 50,
                top: event.clientY,
              };

              const pos = view.posAtCoords(coords);

              if (pos) {
                let node: any = view.domAtPos(pos?.pos);
                if (node) {
                  // find the heading node at the current position
                  node = getHeadingNode(node.node);

                  // if node is not a heading, exit early
                  if (!node && linkElement) {
                    deleteLinkHandle(linkElement);
                    linkElement = null;
                    return false;
                  }

                  // get the HTML attributes defined in the renderHTML method in Heading.ts
                  const headingID = node?.getAttribute("id");
                  const headingType = node?.getAttribute("heading-type");
                  const headingLevel = node?.getAttribute("level");

                  // if no link handle exists yet or was removed, create one attached to heading node
                  if (!linkElement) {
                    linkElement = createLinkHandle(
                      position,
                      headingID,
                      headingType === "title",
                      headingLevel,
                      node
                    );
                  }

                  if (linkElement.element instanceof HTMLElement && node instanceof Element) {
                    // the link handle is positioned relative to the heading node
                    node.classList.add("relative");

                    // set the position
                    linkElement.element.style.top = "0";
                    linkElement.element.style.left = "-34px";
                  } else {
                    // we're not over a valid node, so make handle disappear
                    if (linkElement) {
                      deleteLinkHandle(linkElement);
                      linkElement = null;
                    }
                  }
                }
              }
              return false;
            },

            mousewheel: () => {
              // removes the link handle when scrolling to prevent it from being stuck in one position when the window is scrolled
              if (linkElement) {
                deleteLinkHandle(linkElement);
                linkElement = null;
              }
            },
          },
        },
      }),
    ];
  },
});

interface Heading {
  id: string;
}

const headingRegex = /^H[1-6]$/;

function setHeadingIds(view: EditorView) {
  const _headings: Heading[] = [];
  let transaction = view.state.tr;

  // traverse the document and get all heading nodes
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "heading") {
      const baseId = slugify(node.textContent);
      let id = baseId;
      let counter = 1;

      // id already exists, append number to make it unique
      while (isDuplicateId(id, _headings)) {
        counter += 1;
        id = `${baseId}-${counter}`;
      }

      _headings.push({
        id: id,
      });

      // if not already set, set the id attr on the node (updates DOM)
      if (node.attrs.id !== id) {
        transaction = transaction.setNodeAttribute(pos, "id", id);
      }
    }
  });

  // update view if any headings were changed
  if (transaction.docChanged) {
    transaction.setMeta("addToHistory", false);
    transaction.setMeta("preventUpdate", true);
    view.dispatch(transaction);
  }
}

function isDuplicateId(id: string, _headings: Heading[]) {
  return _headings.some((heading) => heading.id === id);
}

function createLinkHandle(
  position: string,
  nodeId: string,
  isTitle: boolean,
  level: number,
  parentElem: HTMLElement | null
) {
  // create the DOM element which will contain the svelte component
  const element = document.createElement("div");
  element.classList.add(position);

  if (parentElem) {
    parentElem.appendChild(element);
  }

  // create an instance of the svelte component
  const component = new LinkHandle({ target: element, props: { nodeId, isTitle, level } });
  // (we need to keep a reference to both so we can destroy them later)
  return { element, component };
}

function deleteLinkHandle(elem: { element: Element | null; component: SvelteComponent }) {
  // remove the DOM element and destroy the svelte component
  if (elem?.element) elem.element.remove();
  if (elem?.component) elem.component.$destroy();
}

/** Recurses up the DOM tree to find the closest heading node (H1-H6) */
function getHeadingNode(node: Element): Element | undefined {
  // exit early if we're at the top of the editor
  if (node instanceof Element && node.classList.contains("ProseMirror")) return undefined;

  // found a heading, success!
  if (!!node.nodeName && headingRegex.test(node?.nodeName)) {
    return node;
  }

  // check if parent is a heading
  if (node.parentNode) {
    return getHeadingNode(node.parentNode as Element);
  }

  // failed to find heading
  return undefined;
}
